/* import __COLOCATED_TEMPLATE__ from './postings-results-list-item.hbs'; */
import { action, computed } from '@ember/object';
import { bool, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import { ASSIGNMENT } from "../../models/assignment";
import { showModal } from "sh-www/utils/modal-controls";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { EMBER_ROUTE_NAMES } from "../../constants/routes";
import { stopClickEvent } from "../../utils/events";
import clsx from 'clsx';

import { POSTING_CREATE__QUERY_PARAMS } from "../../constants/routes/posting-create";

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: '',

  shClassNames: computed(
    'class',
    '_hasMouseEntered',
    function() {
      return clsx(
        this.class,
        'SH-c-postings-results-list-item',
        this._hasMouseEntered && 'has-mouse'
      );
  }),

  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  companyCommandService: service('commands/company-commands'),
  assignmentCommandService: service('commands/assignment-commands'),
  moment: service(),
  state: service('states/routes/postings'),
  stateManager: service('state-managers/routes/postings'),
  store: service(),
  router: service(),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * The assignment model for this component passed in from caller.
   *
   * @param {AssignmentSummary|null}
   */
  assignment: null,

  currentUserCompanyTeams: [],

  updatedCompanyTeamAssociation: null,

  /**
   * @inheritDoc
   */
  onMouseEnter: action(function() {
    this.set('_hasMouseEntered', true);
  }),

  /**
   * @inheritDoc
   */
  onMouseLeave: action(function() {
    this.set('_hasMouseEntered', false);
  }),

  /**
   * The date timestamp for the relevant date which will be a function of the status on which date to show.
   *
   * @type {String}
   */
  _assignmentDateTimestamp: computed(
  'assignment.status',
  'assignment.publishedAt',
  'assignment.createdAt',
  'assignment.updatedAt',
  function() {
    let momentService = this.get('moment');

    switch (this.get('assignment.status')) {
      case ASSIGNMENT.STATUS.LIVE:
      case ASSIGNMENT.STATUS.CLOSED:
        return momentService.moment(this.get('assignment.publishedAt')).format('MM/DD/YYYY');
      case ASSIGNMENT.STATUS.DRAFT:
        return momentService.moment(this.get('assignment.createdAt')).format('MM/DD/YYYY');
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return momentService.moment(this.get('assignment.updatedAt')).format('MM/DD/YYYY');
    }
  }),

  /**
   * The class for the assignment status element
   *
   * @type {String}
   */
  _assignmentStatusClass: computed('assignment.status', function() {
    switch (this.get('assignment.status')) {
      case ASSIGNMENT.STATUS.LIVE:
        return 'lsg-green';
      case ASSIGNMENT.STATUS.DRAFT:
        return 'lsg-black';
      case ASSIGNMENT.STATUS.CLOSED:
        return 'lsg-medium-gray';
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return 'lsg-dark-gray';
    }
  }),

  /**
   * The status text of the assignment.
   *
   * @type {String}
   */
  _assignmentStatusText: computed('assignment.status', function() {
    switch (this.get('assignment.status')) {
      case ASSIGNMENT.STATUS.LIVE:
        if (this.get('assignment.isAwaitingDirectOffer')) {
          return 'Awaiting your offer';
        }

        return 'Live';
      case ASSIGNMENT.STATUS.DRAFT:
        return 'Draft';
      case ASSIGNMENT.STATUS.CLOSED:
        return 'Closed';
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return 'In Review';
    }
  }),

  /**
   * Return the the assignment uri for editing.
   *
   * @type {String}
   */
  _editAssignmentUri: computed('assignment.slug', function() {
    return `/posting/${this.get('assignment.slug')}/edit`;
  }),

  /**
   * True when the mouse has entered our element.
   *
   * @type {Boolean}
   * @private
   */
  _hasMouseEntered: false,

  /**
   * Will be set to true when the action button is being hovered.
   *
   * @type {Boolean}
   * @private
   */
  _isActionButtonHovered: false,

  /**
   * True when the assignment title should be underlined.
   *
   * @type {Boolean}
   * @private
   */
  _isAssignmentUnderlined: computed('_isActionButtonHovered', '_hasMouseEntered', function() {
    return !this.get('_isActionButtonHovered') && this.get('_hasMouseEntered');
  }),

   /**
   * True if viewing drafts
   *
   * @type {Boolean}
   * @private
   */
   _isViewingDrafts: computed('assignment.status', function() {
    return this.assignment.status === ASSIGNMENT.STATUS.DRAFT;
  }),

  /**
   * True when this assignment should show the 'needs action' indicator
   *
   * @type {Boolean}
   * @private
   */
  _isShowNeedsAction: computed('assignment.isNeedsAction', function() {
    return this.get('assignment.isNeedsAction');
  }),

  /**
   * True when we want to show the stand alone edit button
   *
   * @type {Boolean}
   * @private
   */
  _isShowStandAloneEditButton: computed('assignment.status', function() {
    return this.get('assignment.status') === ASSIGNMENT.STATUS.DRAFT;
  }),

  /**
   * True when we want to show the user bubbles
   *
   * @type {Boolean}
   * @private
   */
  _isShowUserBubbles: computed('assignment.status', function() {
    let statusesWithUserBubbles = [
      ASSIGNMENT.STATUS.LIVE,
      ASSIGNMENT.STATUS.CLOSED
    ];

    return statusesWithUserBubbles.includes(this.get('assignment.status'));
  }),

  /**
   * This is what we'll show for the last activity timestamp dependent on the different statuses.
   *
   * @type {String}
   */
  _lastActivityTimestamp: computed(
  'assignment.lastActivity',
  function() {
    let momentService = this.get('moment');
    return momentService.moment(this.get('assignment.lastActivity')).fromNow();
  }),

  /**
   * @type {Boolean} Whether this assignment can be duplicated.
   */
  _canBeDuplicated: readOnly('currentUser.isBuyer'),

  _companyTeamAssociation: computed(
    'updatedCompanyTeamAssociation',
    'assignment.companyTeam',
    function() {
      return this.updatedCompanyTeamAssociation ?? this.assignment.companyTeam;
    }
  ),

  _availableTeamDropdownOptions: computed(
    'currentUserCompanyTeams',
    'assignment',
    function() {
      return this.currentUserCompanyTeams.reduce((accum, companyTeam) => {
        accum.push({
          title: companyTeam.name,
          onClick: () => this.saveCompanyTeamAssociation.perform(this.assignment, companyTeam),
        });
        return accum;
      }, []);
    }
  ),

  /**
   * The array of menu dropdown options in the format:
   *
   * [{
   *    title: 'Edit',
   *    onClick: Function   // callback function for the click on this menu item
   *  },{...}]
   *
   * @type {Ember.MutableArray}
   */
  _menuDropdownOptions: computed(
  'posting',
  'assignment.watcherCount',
  'assignment.status',
  function() {
    const menuOptions = [];
    const assignmentStatus = this.assignment.status;

    if (assignmentStatus !== ASSIGNMENT.STATUS.DRAFT) {
      menuOptions.push({
        title: 'Edit',
        onClick: () => this._onAssignmentEditClicked()
      });
    }

    const countText = this.assignment.watcherCount ? ` (${this.assignment.watcherCount})` : '';
    menuOptions.push({
      title: `Add Followers${countText}`,
      onClick: () => this._onAddFollowersClicked()
    });

    if (this._canBeDuplicated) {
      menuOptions.push({
        title: 'Duplicate',
        onClick: () => {
          const assignmentId = this.assignment.id;

          this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__CREATE, {
            queryParams: {
              [POSTING_CREATE__QUERY_PARAMS.CLONE_ID]: assignmentId,
            },
          });
        }
      });
    }

    if (assignmentStatus !== ASSIGNMENT.STATUS.CLOSED) {
      menuOptions.push({
        title: 'Close Posting',
        onClick: () => this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__CLOSE, this.assignment.idWithSlug)
      });
    }

    return menuOptions;
  }),

  saveCompanyTeamAssociation: task(function *(assignment, companyTeam) {
    try {
      yield this.assignmentCommandService.setCompanyTeam(assignment.id, companyTeam.id);
      yield this.assignmentCommandService.loadAssignment(assignment.id, ['company-team']);
      this.set('updatedCompanyTeamAssociation', companyTeam);
    } catch(e) {
      this.applicationStateManager.addToast('Cannot update this posting\'s team');
    }
  }).drop(),

  /**
   * Loads the members of the company.
   *
   * @param {String}
   * @param {Assignment}
   *
   * @return {Ember.RSVP.Promise}
   */
  loadPotentialFollowersTask: task( function *(companyId, subject) {
    const companyPromise = yield this.companyCommandService.loadCompany(
      companyId,
      [
        'approved-employees',
      ]
    );

    // Need to fetch buyer since it is not part of the assignment-summary model
    const assignmentPromise = yield this.assignmentCommandService.loadAssignment(
      subject.id,
      [
        'buyer',
      ]
    );

    const [company, assignment] = yield Promise.all([companyPromise, assignmentPromise]);

    const employees = company?.approvedEmployees ?? [];
    const potentialFollowers = employees.filter(({ id }) => {
      return this.currentUser.id !== id && // current user not allowed in list
      assignment.buyer.id !== id; // the buyer can NOT be removed
    });

    return potentialFollowers;
  }).drop(),

  /**
   * Loads the followers of the assignment.
   *
   * @param {String}
   *
   * @return {Ember.RSVP.Promise}
   */
  loadCurrentFollowersTask: task( function *(subjectId) {
    const assignment = yield this.assignmentCommandService.loadAssignment(
      subjectId,
      [
        'followers',
      ]
    );
    return assignment.followers;
  }).drop(),

  /**
   * Saves the new follower array members.
   *
   * @param {Ember.MutableArray}
   * @param {Ember.MutableArray}
   * @param {String}
   */
  saveFollowersTask: task(function *(newUsersSelected, newUsersDeselected, subjectId) {
    const pending = [];

    if (newUsersSelected.length > 0) {
      pending.push(this.assignmentCommandService.addFollowers(
        subjectId, newUsersSelected, ['followers']));
    }
    if (newUsersDeselected.length > 0) {
      pending.push(this.assignmentCommandService.removeFollowers(
        subjectId, newUsersDeselected));
    }

    try {
      yield Promise.all(pending);
      this.applicationStateManager.addToast('Followers updated');
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        'There was an error updating the followers. Please refresh and try again.'
      );
    }
  }).enqueue(),

  _onAssignmentEditClicked: action(function(e) {
    stopClickEvent(e);
    this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__EDIT, this.assignment.idWithSlug);
  }),

  _onAddFollowersClicked: action(function() {
    const modalModel = {
      title: 'Followers of this posting',
      subTitle: 'Select who should be notified of any changes or messages on this posting and related projects',
      loadPotentialFollowersTask: this.loadPotentialFollowersTask,
      loadCurrentFollowersTask: this.loadCurrentFollowersTask,
      saveFollowersTask: this.saveFollowersTask,
      companyId: this.currentUser.company.id,
      subject: this.assignment
    };

    showModal(this, 'add-followers-modal', modalModel);
  }),
});
